import { inject, signal } from '@angular/core';
import { AbstractControl, ValidationErrors } from '@angular/forms';
import { VatResponse, VatResponseStatus } from '@shared/billing/billing.model';
import { BillingService } from '@shared/billing/billing.service';
import { catchError, from, map, Observable, of, switchMap, timer } from 'rxjs';

/**
 * Validates a VAT number against ChargeBee. The request state is exposed alongside the validator
 * rather than stored on the validated control: `ValidationErrors` can only carry a verdict, while
 * the billing form also has to render the in-flight spinner and the "valid" checkmark.
 *
 * Call in an injection context, once per control, and bind the returned `validate`.
 */
export const useVatValidator = (countryControl: AbstractControl) => {
  const billingService = inject(BillingService);

  const validating = signal(false);
  const status = signal<VatResponseStatus | undefined>(undefined);

  let lastVatNumber: string | undefined;

  const validateVatNumber = (control: AbstractControl) => {
    // A previous run is unsubscribed when the value changes again, which can leave the flag set
    // while its request is still in flight.
    validating.set(false);

    const vatNumber = control.value;

    if (vatNumber === lastVatNumber) {
      // Ladda directive triggers a new validation when the button is clicked
      // but the value hasn't changed, so this will prevent the request
      return of(status() === VatResponseStatus.INVALID ? { invalidVat: true } : null);
    }

    lastVatNumber = vatNumber;

    if (!vatNumber || vatNumber.length < 5) {
      status.set(VatResponseStatus.UNDETERMINED);
      return of(null);
    }

    // Remove country code (if present) and update field value
    // This will trigger a value change event and the validator will be called again
    const country = countryControl.value;
    if (vatNumber.startsWith(country)) {
      control.patchValue(vatNumber.slice(country.length));
      status.set(VatResponseStatus.UNDETERMINED);
      return of(null);
    }

    validating.set(true);

    const payload = { country, vat_number: vatNumber };
    const cbInstance = billingService.getChargeBeeInstance();
    // ChargeBee ships no types, so its response shape is asserted here rather than at every read.
    const requestVatValidation = (): Promise<VatResponse> => cbInstance.vat.validateVat(payload);

    return from(cbInstance.load('functions')).pipe(
      switchMap(() => from(requestVatValidation())),
      map((vatResponse) => {
        validating.set(false);
        status.set(vatResponse.status);

        return vatResponse.status === VatResponseStatus.INVALID ? { invalidVat: true } : null;
      }),
      catchError(() => {
        validating.set(false);
        status.set(VatResponseStatus.INVALID);
        return of({ invalidVat: true });
      }),
    );
  };

  const validate = (control: AbstractControl): Observable<ValidationErrors | null> =>
    // Emulate debounceTime and distinctUntilChanged
    // with timer and filter because they don't work with async validators
    timer(1000).pipe(switchMap(() => validateVatNumber(control)));

  return { validating, status, validate };
};
